import { Component } from '@gondel/core';
import { GondelJqueryComponent } from '@gondel/plugin-jquery';
import eventBus, { SLEvent } from '@swisslife/sl-event-bus/src/sl-event-bus';
import { browserDeviceTests } from '@swisslife/shared-ui/src/utils/browser-detection/js/browser-detection';

export interface IBreadcrumbItem {
	name: string;
	text: string;
	href: string;
	onClick?: (event: Event) => void;
}

enum ClassNames {
	Item = 'js-m-breadcrumb__item',
}

enum States {
	Animate = 'state-m-breadcrumb--animate',
}

enum Selectors {
	List = '.js-m-breadcrumb__list',
	Item = '.js-m-breadcrumb__item',
	ItemLast = '.js-m-breadcrumb__item--last',
	HomeItem = '.js-m-breadcrumb__item-home',
	SmallItem = '.js-m-breadcrumb__item-small',
}

const excludeUndefined = <T>(val: T): val is Exclude<T, undefined> => val !== undefined;

@Component('Breadcrumb')
export class Breadcrumb extends GondelJqueryComponent {
	private template: string = this.$ctx.data('template');
	private $itemHomeOriginal: JQuery = $(Selectors.HomeItem, this.$ctx).clone();
	private $itemLastOriginal: JQuery = $(Selectors.ItemLast, this.$ctx).clone();
	private $itemSmallOriginal: JQuery = $(Selectors.SmallItem, this.$ctx).clone();
	private itemsOriginal: IBreadcrumbItem[];
	private items: IBreadcrumbItem[];

	public start() {
		const $items = $(Selectors.Item, this.$ctx);
		this.itemsOriginal = $items
			.toArray()
			.map((item, index) => {
				if (index === $items.length - 1) {
					return;
				}
				const text = $('span.a-link__text', item).html();
				return { name: text, text, href: $('a', item).attr('href') || '' };
			})
			.filter(excludeUndefined);
		this.items = this.itemsOriginal;
	}

	public async sync() {
		const eb = await eventBus();
		eb.on(SLEvent.ChangeBreadcrumb, this.handleChangeBreadcrumb);
	}

	public toggleStateClass(stateToToggle: string, forceState?: boolean) {
		if (forceState !== undefined) {
			this.$ctx.toggleClass(States[stateToToggle], forceState);
		} else {
			this.$ctx.toggleClass(States[stateToToggle]);
		}
	}

	private handleChangeBreadcrumb = (meta: string, items: IBreadcrumbItem[]) => {
		if (!Array.isArray(items)) {
			return;
		}
		this.items = [...this.itemsOriginal, ...items];
		this.unbindClickHandler();
		this.render();
		this.bindClickHandler();
	};

	private render() {
		const list: JQuery[] = [];
		this.items.forEach((item, index) => {
			if (index === this.items.length - 1) {
				list.push(this.createLastItem(item).attr('data-index', index));
				return;
			}
			list.push(this.createItem(item).attr('data-index', index));
		});
		if (this.items[this.items.length - 2]) {
			list.push(this.createSmallItem(this.items[this.items.length - 2]));
		} else {
			list.push(
				this.createSmallItem({
					href: this.$itemHomeOriginal.find('a').attr('href') || '/',
				})
			);
		}
		$(Selectors.Item, this.$ctx).remove();
		$(Selectors.SmallItem, this.$ctx).remove();
		$(Selectors.List, this.$ctx).append(list);
	}

	private unbindClickHandler = () => {
		$(Selectors.Item, this.$ctx).each((index, elem) => {
			$(elem).off('click.item');
		});
		$(`${Selectors.SmallItem} a`, this.$ctx).off('click.item');
	};

	private bindClickHandler = () => {
		$(Selectors.Item, this.$ctx)
			.not(Selectors.ItemLast)
			.toArray()
			.forEach((elem) => {
				const onClick = this.items[$(elem).data('index')].onClick;
				if (typeof onClick === 'function') {
					$(elem).on('click.item', onClick);
				}
			});
		if (!this.items[this.items.length - 2]) {
			return;
		}
		const onClickSmall = this.items[this.items.length - 2].onClick;
		if (typeof onClickSmall !== 'function') {
			return;
		}
		$(`${Selectors.SmallItem} a`, this.$ctx).on('click.item', onClickSmall);
	};

	private createItem = ({ text, href }: IBreadcrumbItem) => {
		const $item = $(this.template.replace('{text}', text).replace('{href}', href));
		$item.addClass(ClassNames.Item);
		return $item;
	};

	private createLastItem = ({ text }: { text: string }) => {
		const $item = this.$itemLastOriginal.clone();
		$item.find('.a-text').html(text);

		// IE11 workaround (svg inner serialization)
		if (browserDeviceTests.ie) {
			const $svgInner = `<use
				xlink:href="/etc.clientlibs/slevo/clientlibs/site/resources/sprites.svg#arrow-right-16">
			</use>`;
			$item.find('.a-svg').append($svgInner);
		}

		return $item;
	};

	private createSmallItem = ({ href }: { href: string }) => {
		const $item = this.$itemSmallOriginal.clone();
		$item.find('a').attr('href', href);
		return $item;
	};
}
